import { readRawCookieFromDocument, readRawCookieValue } from './cookie';

/** Converge Cookie Manager 持久化用户选择的 cookie。 */
export const ATTRIBUTION_CONSENT_COOKIE_NAME = 'converge_cookie_consent';

/** 注册归因使用的最小 consent 分类。 */
export interface AttributionConsent {
  analytics: boolean;
  advertising: boolean;
}

/**
 * 与 Cookie Manager `Data` 结构兼容的最小输入。
 * 共享包只依赖归因需要的字段，避免反向依赖 UI SDK。
 */
export interface AttributionConsentData {
  hasChoice: boolean;
  mode: 'opt_in' | 'notice_only';
  preference: {
    analytics: boolean;
    advertising: boolean;
  } | null;
}

const DENIED_ATTRIBUTION_CONSENT: AttributionConsent = {
  analytics: false,
  advertising: false,
};

function decodeConsentCookie(rawValue: string): unknown {
  try {
    return JSON.parse(decodeURIComponent(rawValue));
  } catch {
    return null;
  }
}

function isAttributionConsentData(
  value: unknown,
): value is AttributionConsentData {
  if (!value || typeof value !== 'object') return false;

  const data = value as Record<string, unknown>;
  const preference = data.preference;
  if (
    typeof data.hasChoice !== 'boolean' ||
    (data.mode !== 'opt_in' && data.mode !== 'notice_only') ||
    !preference ||
    typeof preference !== 'object'
  ) {
    return false;
  }

  const preferenceRecord = preference as Record<string, unknown>;
  return (
    typeof preferenceRecord.analytics === 'boolean' &&
    typeof preferenceRecord.advertising === 'boolean'
  );
}

/** 将 Cookie Manager 当前内存状态映射为注册归因 consent。 */
export function resolveAttributionConsent(
  data: AttributionConsentData | null | undefined,
): AttributionConsent {
  if (!data?.preference) {
    return { ...DENIED_ATTRIBUTION_CONSENT };
  }

  return {
    analytics: data.preference.analytics === true,
    advertising: data.preference.advertising === true,
  };
}

function parseSavedAttributionConsent(
  rawValue: string | null,
): AttributionConsent {
  if (!rawValue) {
    return { ...DENIED_ATTRIBUTION_CONSENT };
  }

  const payload = decodeConsentCookie(rawValue);
  if (!payload || typeof payload !== 'object') {
    return { ...DENIED_ATTRIBUTION_CONSENT };
  }

  const record = payload as Record<string, unknown>;
  if (
    record.v !== 1 ||
    !record.data ||
    !isAttributionConsentData(record.data)
  ) {
    return { ...DENIED_ATTRIBUTION_CONSENT };
  }

  // 持久化 cookie 只接受用户已经保存的选择；伪造的未选择状态按默认拒绝处理。
  if (!record.data.hasChoice) {
    return { ...DENIED_ATTRIBUTION_CONSENT };
  }

  return resolveAttributionConsent(record.data);
}

/** Worker / Request 侧读取已经保存的归因 consent；缺失或损坏时默认拒绝。 */
export function readAttributionConsentFromCookieHeader(
  headers: Headers,
): AttributionConsent {
  const cookieHeader = headers.get('Cookie') ?? headers.get('cookie');
  return parseSavedAttributionConsent(
    readRawCookieValue(cookieHeader, ATTRIBUTION_CONSENT_COOKIE_NAME),
  );
}

/** 浏览器侧读取已经保存的归因 consent；缺失或损坏时默认拒绝。 */
export function readAttributionConsentFromDocument(): AttributionConsent {
  return parseSavedAttributionConsent(
    readRawCookieFromDocument(ATTRIBUTION_CONSENT_COOKIE_NAME),
  );
}
